import React, { useEffect, useMemo, useState } from 'react';

/**
 * NumberInput - thousand separator formatted numeric input
 * props:
 *  - value: number | string
 *  - onChange: (number) => void
 *  - allowDecimal?: boolean (default false)
 *  - min, max, step, placeholder, className, readOnly, disabled
 */
const NumberInput = ({
  value,
  onChange,
  allowDecimal = false,
  min,
  max,
  step,
  placeholder,
  className,
  readOnly = false,
  disabled = false,
}) => {
  const formatDisplay = (val) => {
    if (val === null || val === undefined || val === '') return '';
    const str = String(val);
    // split integer and decimal
    const [intPartRaw, decPartRaw] = str.replace(/,/g, '').split('.');
    const intPartNum = intPartRaw === '' ? 0 : parseInt(intPartRaw, 10);
    const intPart = isNaN(intPartNum) ? '0' : intPartNum.toLocaleString();
    if (allowDecimal && decPartRaw !== undefined) {
      return `${intPart}.${decPartRaw}`;
    }
    return intPart;
  };

  const [display, setDisplay] = useState(formatDisplay(value));

  useEffect(() => {
    setDisplay(formatDisplay(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, allowDecimal]);

  const handleChange = (e) => {
    let input = e.target.value;
    // Allow only digits and optional single dot
    if (allowDecimal) {
      // Remove invalid chars
      input = input.replace(/[^\d.]/g, '');
      // keep only first dot
      const parts = input.split('.');
      if (parts.length > 2) {
        input = `${parts[0]}.${parts.slice(1).join('')}`;
      }
    } else {
      input = input.replace(/[^\d]/g, '');
    }

    // 빈 문자열이면 UI에서는 공백 유지해 입력을 시작할 수 있게 함
    if (input === '') {
      setDisplay('');
      onChange(0);
      return;
    }
    // 소수 허용시 '.'만 입력한 중간 상태 처리
    if (allowDecimal && input === '.') {
      setDisplay('0.');
      onChange(0);
      return;
    }

    // Format for display
    const [intRaw, decRaw] = input.split('.');
    const intNum = intRaw === '' ? 0 : parseInt(intRaw, 10);
    const intFormatted = isNaN(intNum) ? '' : intNum.toLocaleString();
    const nextDisplay = allowDecimal && decRaw !== undefined ? `${intFormatted}.${decRaw}` : intFormatted;
    setDisplay(nextDisplay);

    // Emit numeric value
    const num = allowDecimal ? parseFloat(input || '0') : parseInt(input || '0', 10);
    onChange(isNaN(num) ? 0 : num);
  };

  const handleBlur = () => {
    setDisplay(formatDisplay(value));
  };

  return (
    <input
      type="text"
      inputMode={allowDecimal ? 'decimal' : 'numeric'}
      value={display}
      onChange={handleChange}
      onBlur={handleBlur}
      className={className}
      placeholder={placeholder}
      readOnly={readOnly}
      disabled={disabled}
      min={min}
      max={max}
      step={step}
    />
  );
};

export default NumberInput; 